import React, { createContext, useContext, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { resolveDrop, RESULT_MESSAGES } from '../../../shared/playtest';

/** Drag payload type. Firefox only starts a drag when some data is set. */
const DRAG_TYPE = 'application/x-playtest-instance';
const DROP_ACCEPT_OUTLINE = '2px dashed #52c41a';
const DROP_REJECT_OUTLINE = '2px dashed #ff4d4f';
const DROP_STATE = { accept: 'accept', reject: 'reject' };
const DROP_OUTLINE = { [DROP_STATE.accept]: DROP_ACCEPT_OUTLINE, [DROP_STATE.reject]: DROP_REJECT_OUTLINE };

/** The command zone as a drop or move destination. */
export const COMMAND_TO = 'command';

const DragStoreContext = createContext(null);

const sameTarget = (a, b) => a.kind === b.kind && a.zone === b.zone && a.instanceId === b.instanceId;

/**
 * The engine's decision for dropping `instanceId` on `target`: an accepted
 * DropResult, a reject, or null when no card is being dragged. A card never
 * drops on itself (a DOM-only guard); every other rule comes from `resolveDrop`.
 */
export const dropOutcome = (state, instanceId, target) => {
  if (instanceId === null) return null;
  if (target.kind === 'card' && target.instanceId === instanceId) return { kind: 'reject', reason: 'same-zone' };
  return resolveDrop(state, instanceId, target);
};

/** The accepted decision for a drop, or null when the drop is not allowed. */
export const dropResultFor = (state, instanceId, target) => {
  const outcome = dropOutcome(state, instanceId, target);
  return outcome?.kind === 'reject' ? null : outcome;
};

/** The refusal text for a card that cannot drop on `target`, or null when there is none. */
export const refusalMessage = (state, instanceId, target) => {
  const outcome = dropOutcome(state, instanceId, target);
  return outcome?.kind === 'reject' ? RESULT_MESSAGES[outcome.reason] : null;
};

/**
 * Drag state for one playtest page: the instance being dragged and the engine
 * decision for the hover in progress. `decide` runs the engine once per
 * (engine state, target), so a hover and the drop that follows share one
 * decision. A new drag, a drop, or a different state or target recomputes it.
 */
export const createDragStore = () => {
  let instanceId = null;
  let decision = null;
  return {
    begin(id) {
      instanceId = id;
      decision = null;
    },
    /** Ends the drag and returns the instance it carried, or null. */
    end() {
      const ended = instanceId;
      instanceId = null;
      decision = null;
      return ended;
    },
    current: () => instanceId,
    decide(state, target) {
      if (decision !== null && decision.state === state && sameTarget(decision.target, target)) {
        return decision.outcome;
      }
      const outcome = dropOutcome(state, instanceId, target);
      decision = { state, target, outcome };
      return outcome;
    },
  };
};

const storeShape = PropTypes.shape({
  begin: PropTypes.func.isRequired,
  end: PropTypes.func.isRequired,
  current: PropTypes.func.isRequired,
  decide: PropTypes.func.isRequired,
});

/**
 * Supplies a page's drag store to its drag sources and drop zones. While mounted,
 * a window `dragend` or `drop` ends the drag: a drop outside every zone, or a
 * source that unmounted, never reaches its own handlers. A drop on a zone stops
 * propagation before the window, so the zone's handler ends the drag instead.
 * Unmounting removes the listeners and ends any drag still in progress.
 */
export const PlaytestDragProvider = ({ store, children }) => {
  useEffect(() => {
    const endDrag = () => store.end();
    window.addEventListener('dragend', endDrag);
    window.addEventListener('drop', endDrag);
    return () => {
      window.removeEventListener('dragend', endDrag);
      window.removeEventListener('drop', endDrag);
      store.end();
    };
  }, [store]);
  return <DragStoreContext.Provider value={store}>{children}</DragStoreContext.Provider>;
};

PlaytestDragProvider.propTypes = {
  store: storeShape.isRequired,
  children: PropTypes.node,
};

PlaytestDragProvider.defaultProps = {
  children: null,
};

const useDragStore = () => {
  const store = useContext(DragStoreContext);
  if (store === null) throw new Error('Playtest drag sources and drop zones must render inside a PlaytestDragProvider');
  return store;
};

/**
 * Returns `dragSourceProps(instanceId, handlers)` from `useDragSourceProps()`, which makes a tile a drag source
 * for the enclosing page. `handlers.onStart` and `handlers.onEnd` run after the drag
 * is recorded and after it is cleared, so a dialog can react to its own card being dragged.
 */
export const useDragSourceProps = () => {
  const drag = useDragStore();
  return (instanceId, handlers = {}) => ({
    draggable: true,
    onDragStart: (event) => {
      drag.begin(instanceId);
      event.dataTransfer.effectAllowed = 'move';
      event.dataTransfer.setData(DRAG_TYPE, String(instanceId));
      handlers.onStart?.(instanceId);
    },
    onDragEnd: () => {
      drag.end();
      handlers.onEnd?.(instanceId);
    },
  });
};

/**
 * A region that accepts a dragged card. `drops.canDrop(target)` decides whether
 * a hover is accepted, and `drops.onDrop(target)` performs the drop. The region
 * shows accept or reject while a card hovers. Nested drop zones (a card inside
 * a zone) stop the event, so the innermost target decides.
 */
export const PlaytestDropZone = ({ testId, target, drops, style, children }) => {
  const drag = useDragStore();
  const [hover, setHover] = useState(null);

  const onDragOver = (event) => {
    if (drag.current() === null) return;
    event.stopPropagation();
    const accepted = drops.canDrop(target);
    if (accepted) {
      event.preventDefault();
      event.dataTransfer.dropEffect = 'move';
    }
    if (!accepted) drops.onRefuse?.(target);
    setHover(accepted ? DROP_STATE.accept : DROP_STATE.reject);
  };

  const onDragLeave = (event) => {
    event.stopPropagation();
    if (event.currentTarget.contains(event.relatedTarget)) return;
    setHover(null);
  };

  const onDrop = (event) => {
    event.stopPropagation();
    event.preventDefault();
    setHover(null);
    drops.onDrop(target);
  };

  const outline = hover === null ? null : { outline: DROP_OUTLINE[hover] };
  return (
    <div
      data-testid={testId}
      data-drop-state={hover ?? undefined}
      style={{ ...style, ...outline }}
      onDragOver={onDragOver}
      onDragLeave={onDragLeave}
      onDrop={onDrop}
    >
      {children}
    </div>
  );
};

const dropTargetShape = PropTypes.shape({
  kind: PropTypes.oneOf(['zone', 'card']).isRequired,
  zone: PropTypes.string,
  instanceId: PropTypes.number,
});

PlaytestDropZone.propTypes = {
  testId: PropTypes.string.isRequired,
  target: dropTargetShape.isRequired,
  drops: PropTypes.shape({
    canDrop: PropTypes.func.isRequired,
    onDrop: PropTypes.func.isRequired,
    onRefuse: PropTypes.func,
  }).isRequired,
  style: PropTypes.object,
  children: PropTypes.node,
};

PlaytestDropZone.defaultProps = {
  style: undefined,
  children: null,
};
